import { normalizeAccessGrants } from '../ticketing/access-grant-matching.js';

const GRANT_TYPES = new Set(['user', 'role', 'unit', 'title']);

/**
 * @param {unknown} value
 * @returns {value is Array<{ type: string, value: string, label?: string }>}
 */
export function isAccessGrantList(value) {
    if (!Array.isArray(value) || value.length === 0) return false;
    return value.every((item) => {
        if (!item || typeof item !== 'object') return false;
        const type = String(item.type || '').toLowerCase();
        const val = String(item.value || '').trim();
        return GRANT_TYPES.has(type) && Boolean(val);
    });
}

function legacyArrayToUserGrants(value) {
    if (!Array.isArray(value)) return [];
    const grants = [];
    for (const r of value) {
        const id = r && typeof r === 'object' ? (r.userId ?? r._id ?? r.id) : r;
        if (id) grants.push({ type: 'user', value: String(id) });
    }
    return grants;
}

/**
 * Convert legacy RecipientTarget shapes to access-grant list.
 * @param {unknown} value
 * @returns {Array<{ type: string, value: string, label?: string }>}
 */
export function legacyRecipientTargetToGrants(value) {
    if (value == null || value === '') return [];
    if (isAccessGrantList(value)) return normalizeAccessGrants(value);

    if (Array.isArray(value)) {
        return normalizeAccessGrants(legacyArrayToUserGrants(value));
    }

    if (typeof value !== 'object') return [];

    const grants = [];
    const obj = /** @type {Record<string, unknown>} */ (value);

    if (obj.userId) {
        grants.push({ type: 'user', value: String(obj.userId) });
    }
    for (const id of obj.userIds || []) {
        if (id) grants.push({ type: 'user', value: String(id) });
    }
    if (obj.roleId) {
        grants.push({ type: 'role', value: String(obj.roleId) });
    }
    for (const id of obj.roleIds || []) {
        if (id) grants.push({ type: 'role', value: String(id) });
    }
    if (obj.unitId) {
        grants.push({ type: 'unit', value: String(obj.unitId) });
    }
    for (const id of obj.unitIds || []) {
        if (id) grants.push({ type: 'unit', value: String(id) });
    }

    return normalizeAccessGrants(grants);
}

/**
 * Build messenger RecipientTarget summary from grants (title resolved separately at runtime).
 * @param {unknown[]} grants
 * @returns {{ userIds?: string[], roleIds?: string[], unitIds?: string[] }}
 */
export function grantsToRecipientTarget(grants) {
    const normalized = normalizeAccessGrants(grants);
    const userIds = [];
    const roleIds = [];
    const unitIds = [];

    for (const g of normalized) {
        if (g.type === 'user') userIds.push(g.value);
        else if (g.type === 'role') roleIds.push(g.value);
        else if (g.type === 'unit') unitIds.push(g.value);
    }

    const out = {};
    if (userIds.length) out.userIds = [...new Set(userIds)];
    if (roleIds.length) out.roleIds = [...new Set(roleIds)];
    if (unitIds.length) out.unitIds = [...new Set(unitIds)];
    return out;
}

/**
 * @param {unknown} value
 * @returns {Array<{ type: string, value: string, label?: string }>}
 */
export function coerceRecipientsToGrants(value) {
    if (isAccessGrantList(value)) return normalizeAccessGrants(value);
    const legacy = legacyRecipientTargetToGrants(value);
    if (legacy.length) return legacy;
    return [];
}
